import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportSystemParameters } from './api/systemParameterApi'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import RefreshButton from '../../components/ui/RefreshButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { searchTerm } from '../../lib/searchTerm'
import { useToast } from '../../components/ui/ToastProvider'
import { isOptimisticLock } from '../../api/apiError'
import { systemParameterColumns } from './components/SystemParameterColumns'
import SystemParameterFormDialog from './components/SystemParameterFormDialog'
import { useSystemParameters } from './hooks/useSystemParameters'
import type { SystemParameter } from './types'
import Button from '../../components/ui/Button'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import './admin.css'

/**
 * Admin → Sistem Parametreleri (015). Listeleme + yalnız değer güncelleme (ekle/sil yok).
 *
 * <p>Kısıtlı Yetkili asistan (126) ekranı <b>salt okunur</b> görür: Düzenle eylemi tabloda da kartta
 * da çizilmez. Kod, ad ve değer listede zaten göründüğü için görüntüleme pop-up'ına gerek yoktur.
 */
export default function SystemParametersAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const toast = useToast()
  const { readOnly } = useProxyMode()

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setQ(searchInput), 400)
    return () => clearTimeout(id)
  }, [searchInput])

  const { list, update } = useSystemParameters(searchTerm(q))
  const [editing, setEditing] = useState<SystemParameter | null>(null)

  const rows = list.data?.content ?? []
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const onSubmit = (value: string) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { value, modDate: editing.modDate } },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', summary: t('admin.systemParameters.toast.updated'), life: 3000 })
          setEditing(null)
        },
        onError: (err) => {
          // optimistic-lock/generic toast'ı merkez (019) basar; ekran yalnız çakışmada tazeler.
          if (isOptimisticLock(err)) {
            setEditing(null)
            list.refetch()
          }
        },
      },
    )
  }

  return (
    <div className="admin-page">
      <SystemParameterFormDialog
        visible={editing != null}
        initial={editing}
        submitting={update.isPending}
        onSubmit={onSubmit}
        onHide={() => setEditing(null)}
      />

      <PageHeader
        title={t('nav.admin.systemParameters')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA — Figma tablo tasarimi (kullanici istegi 2026-08-26).
                Onceden filtre cubugundaydi (`FilterBar onRefresh`). */}
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={() => exportSystemParameters(searchTerm(q))} disabled={rows.length === 0} />
          </>
        }
      />

      <FilterBar
        searchValue={searchInput}
        searchPlaceholder={t('admin.systemParameters.search')}
        onSearch={setSearchInput}
      />

      {/* `admin-table-wrap`: uzun kod/ad/değer metinleri hücrede alt satıra sarılır → tabloda
          yatay kaydırma çubuğu çıkmaz (kolon genişlikleri `systemParameterColumns`'ta). */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && rows.length === 0 && (
            <EmptyState
              title={t('admin.systemParameters.empty.title')}
              description={t('admin.systemParameters.empty.desc')}
            />
          )}
          {!list.isLoading &&
            rows.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <p className="ui-card-row" style={{ fontWeight: 'bold' }}>{row.value}</p>
                  {!readOnly && (
                    <div className="ui-card-actions ui-card-actions--compact">
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label={t('admin.systemParameters.actions.edit')}
                        onClick={() => setEditing(row)}
                      />
                    </div>
                  )}
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                </div>
                <span className="ui-card-sub">{row.code}</span>
              </div>
            ))}
        </div>
      ) : (
      <DataTable
        className="admin-table-wrap"
        value={rows}
        loading={list.isLoading}
        sortField="code"
        sortOrder={1}
        emptyMessage={
          <EmptyState
            title={t('admin.systemParameters.empty.title')}
            description={t('admin.systemParameters.empty.desc')}
          />
        }
      >
        {systemParameterColumns({ t, localized, onEdit: setEditing, readOnly })}
      </DataTable>
      )}
    </div>
  )
}
